import React from 'react';
import { isGameFinished, isGameLive, BALLPARK_DATA } from '../utils/constants';

// Inline SVG weather icons based on description/conditions
const WeatherIcon = ({ size = 20, description, cloudCover, precipProb, temp }) => {
  const desc = (description || '').toLowerCase();
  const isNight = desc.includes('night') || desc.includes('clear night');

  if (precipProb >= 50 || desc.includes('rain') || desc.includes('shower') || desc.includes('drizzle') || desc.includes('thunderstorm')) {
    // Rain/Storm
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M6 10a4 4 0 0 1 7.8-1.2A3 3 0 0 1 17 12H6a3 3 0 0 1 0-6z" fill="#6b7280" />
        {desc.includes('thunder') && <path d="M13 12l-2 4h3l-2 4" stroke="#ffc107" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />}
        <line x1="8" y1="14" x2="7" y2="18" stroke="#60a5fa" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="12" y1="14" x2="11" y2="18" stroke="#60a5fa" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="16" y1="14" x2="15" y2="18" stroke="#60a5fa" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    );
  }

  if (desc.includes('snow') || desc.includes('flurr')) {
    // Snow
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M6 10a4 4 0 0 1 7.8-1.2A3 3 0 0 1 17 12H6a3 3 0 0 1 0-6z" fill="#9ca3af" />
        <circle cx="8" cy="16" r="1" fill="#e5e7eb" />
        <circle cx="12" cy="15" r="1" fill="#e5e7eb" />
        <circle cx="16" cy="17" r="1" fill="#e5e7eb" />
        <circle cx="10" cy="19" r="1" fill="#e5e7eb" />
        <circle cx="14" cy="19" r="1" fill="#e5e7eb" />
      </svg>
    );
  }

  if (desc.includes('overcast') || (cloudCover >= 80)) {
    // Overcast
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M6 12a4 4 0 0 1 7.8-1.2A3 3 0 0 1 17 14H6a3 3 0 0 1 0-6z" fill="#6b7280" />
        <path d="M9 10a3 3 0 0 1 5.85-.9A2.25 2.25 0 0 1 17.25 11.5H9a2.25 2.25 0 0 1 0-4.5z" fill="#9ca3af" />
      </svg>
    );
  }

  if (desc.includes('partly') || desc.includes('mostly cloudy') || desc.includes('cloudy') || (cloudCover >= 30 && cloudCover < 80)) {
    // Partly cloudy
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="10" cy="9" r="4" fill="#ffc107" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map(angle => {
          const rad = (angle * Math.PI) / 180;
          return <line key={angle} x1={10 + Math.cos(rad) * 5} y1={9 + Math.sin(rad) * 5} x2={10 + Math.cos(rad) * 6.5} y2={9 + Math.sin(rad) * 6.5} stroke="#ffc107" strokeWidth="1.2" strokeLinecap="round" />;
        })}
        <path d="M10 14a3 3 0 0 1 5.85-.9A2.25 2.25 0 0 1 18.25 15.5H10a2.25 2.25 0 0 1 0-4.5z" fill="#9ca3af" />
      </svg>
    );
  }

  if (isNight || desc.includes('clear night')) {
    // Moon
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" fill="#ffc107" />
      </svg>
    );
  }

  // Sunny / Clear (default)
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="4" fill="#ffc107" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map(angle => {
        const rad = (angle * Math.PI) / 180;
        return <line key={angle} x1={12 + Math.cos(rad) * 5.5} y1={12 + Math.sin(rad) * 5.5} x2={12 + Math.cos(rad) * 7} y2={12 + Math.sin(rad) * 7} stroke="#ffc107" strokeWidth="1.5" strokeLinecap="round" />;
      })}
    </svg>
  );
};

// Ballpark weather SVG — field outline with weather info inside
const BallparkWeatherSVG = ({ cfBearing, windFromDeg, windSpeed, size = 70, dome, temp, description, cloudCover, precipProb }) => {
  // Wind direction relative to field: where wind blows TO
  const windBlowingTo = ((windFromDeg + 180 - cfBearing + 360) % 360);

  // Field geometry — home plate at bottom, CF at top
  // Using a proper quarter-circle fan shape
  const homeX = 60, homeY = 95;
  const r = 75; // outfield wall radius from home plate

  // LF and RF foul line endpoints (90° apart, symmetric around CF which is straight up)
  const lfX = homeX + r * Math.cos(225 * Math.PI / 180); // 225° in SVG = upper-left
  const lfY = homeY + r * Math.sin(225 * Math.PI / 180);
  const rfX = homeX + r * Math.cos(315 * Math.PI / 180); // 315° in SVG = upper-right
  const rfY = homeY + r * Math.sin(315 * Math.PI / 180);

  // Outfield arc: sweep from RF to LF (counter-clockwise in SVG = sweep-flag 0)
  const fieldPath = `M ${homeX} ${homeY} L ${rfX} ${rfY} A ${r} ${r} 0 0 0 ${lfX} ${lfY} Z`;

  // Wind arrow from center of field
  const arrowCx = homeX, arrowCy = homeY - 35;
  const arrowLen = 24;
  const rad = (windBlowingTo - 90) * Math.PI / 180;
  const ax = arrowCx + arrowLen * Math.cos(rad);
  const ay = arrowCy + arrowLen * Math.sin(rad);
  const headLen = 7;
  const headAngle = 25 * Math.PI / 180;
  const h1x = ax - headLen * Math.cos(rad - headAngle);
  const h1y = ay - headLen * Math.sin(rad - headAngle);
  const h2x = ax - headLen * Math.cos(rad + headAngle);
  const h2y = ay - headLen * Math.sin(rad + headAngle);

  // Arrow color based on wind direction relative to field
  let arrowColor = '#9ca3af';
  if (windSpeed >= 3) {
    if (windBlowingTo <= 50 || windBlowingTo >= 310) arrowColor = '#28a745'; // out
    else if (windBlowingTo >= 140 && windBlowingTo <= 220) arrowColor = '#dc3545'; // in
    else arrowColor = '#F59E0B'; // cross
  }

  // Temp color
  const tempColor = temp <= 40 ? '#60a5fa' : temp >= 85 ? '#dc3545' : '#dee2e6';

  // Weather icon type
  const desc = (description || '').toLowerCase();
  const isRain = precipProb >= 50 || desc.includes('rain') || desc.includes('shower') || desc.includes('drizzle') || desc.includes('thunder');
  const isSnow = desc.includes('snow') || desc.includes('flurr');
  const isOvercast = desc.includes('overcast') || cloudCover >= 80;
  const isCloudy = desc.includes('partly') || desc.includes('mostly cloudy') || desc.includes('cloudy') || (cloudCover >= 30 && cloudCover < 80);

  return (
    <svg width={size} height={size} viewBox="0 0 120 110">
      {/* Field fill */}
      <path d={fieldPath} fill="rgba(34,197,94,0.10)" />
      {/* Foul lines */}
      <line x1={homeX} y1={homeY} x2={lfX} y2={lfY} stroke="#4b5563" strokeWidth="1.2" />
      <line x1={homeX} y1={homeY} x2={rfX} y2={rfY} stroke="#4b5563" strokeWidth="1.2" />
      {/* Outfield wall arc */}
      <path d={`M ${rfX} ${rfY} A ${r} ${r} 0 0 0 ${lfX} ${lfY}`} fill="none" stroke="#4b5563" strokeWidth="1.2" />
      {/* Infield diamond */}
      <polygon points={`${homeX},${homeY} ${homeX - 10},${homeY - 10} ${homeX},${homeY - 20} ${homeX + 10},${homeY - 10}`}
        fill="none" stroke="#4b5563" strokeWidth="0.8" />
      {/* Home plate */}
      <circle cx={homeX} cy={homeY} r="2" fill="#dee2e6" />

      {/* Weather icon at top-center of field */}
      <g transform="translate(60, 18)">
        {isRain ? (
          <>
            <path d="M-8 0a5 5 0 0 1 9.75-1.5A3.75 3.75 0 0 1 5.5 3H-8a3.75 3.75 0 0 1 0-7.5z" fill="#6b7280" />
            <line x1="-4" y1="5" x2="-5" y2="10" stroke="#60a5fa" strokeWidth="1.2" strokeLinecap="round" />
            <line x1="1" y1="5" x2="0" y2="10" stroke="#60a5fa" strokeWidth="1.2" strokeLinecap="round" />
            <line x1="6" y1="5" x2="5" y2="10" stroke="#60a5fa" strokeWidth="1.2" strokeLinecap="round" />
          </>
        ) : isSnow ? (
          <>
            <path d="M-8 0a5 5 0 0 1 9.75-1.5A3.75 3.75 0 0 1 5.5 3H-8a3.75 3.75 0 0 1 0-7.5z" fill="#9ca3af" />
            <circle cx="-3" cy="7" r="1.2" fill="#e5e7eb" />
            <circle cx="2" cy="9" r="1.2" fill="#e5e7eb" />
            <circle cx="6" cy="6" r="1.2" fill="#e5e7eb" />
          </>
        ) : isOvercast ? (
          <>
            <path d="M-8 2a5 5 0 0 1 9.75-1.5A3.75 3.75 0 0 1 5.5 5H-8a3.75 3.75 0 0 1 0-7.5z" fill="#6b7280" />
            <path d="M-4 0a3.5 3.5 0 0 1 6.8-1A2.6 2.6 0 0 1 5 1.5H-4a2.6 2.6 0 0 1 0-5.2z" fill="#9ca3af" />
          </>
        ) : isCloudy ? (
          <>
            <circle cx="-2" cy="-2" r="5" fill="#ffc107" />
            {[0, 60, 120, 180, 240, 300].map(a => {
              const rd = a * Math.PI / 180;
              return <line key={a} x1={-2 + Math.cos(rd) * 6.5} y1={-2 + Math.sin(rd) * 6.5} x2={-2 + Math.cos(rd) * 8} y2={-2 + Math.sin(rd) * 8} stroke="#ffc107" strokeWidth="1.2" strokeLinecap="round" />;
            })}
            <path d="M-2 4a3.5 3.5 0 0 1 6.8-1A2.6 2.6 0 0 1 7 5.5H-2a2.6 2.6 0 0 1 0-5.2z" fill="#9ca3af" />
          </>
        ) : (
          <>
            <circle cx="0" cy="0" r="5" fill="#ffc107" />
            {[0, 45, 90, 135, 180, 225, 270, 315].map(a => {
              const rd = a * Math.PI / 180;
              return <line key={a} x1={Math.cos(rd) * 6.5} y1={Math.sin(rd) * 6.5} x2={Math.cos(rd) * 8.5} y2={Math.sin(rd) * 8.5} stroke="#ffc107" strokeWidth="1.5" strokeLinecap="round" />;
            })}
          </>
        )}
      </g>

      {/* Temperature */}
      <text x={homeX} y={48} fill={tempColor} fontSize="14" fontWeight="700" textAnchor="middle" fontFamily="Inter, sans-serif">
        {temp}°
      </text>

      {/* Wind arrow */}
      {windSpeed > 0 && !dome && (
        <>
          <line x1={arrowCx} y1={arrowCy} x2={ax} y2={ay} stroke={arrowColor} strokeWidth="2.5" strokeLinecap="round" />
          <polygon points={`${ax},${ay} ${h1x},${h1y} ${h2x},${h2y}`} fill={arrowColor} />
          <text x={arrowCx} y={arrowCy + 14} fill={arrowColor} fontSize="9" fontWeight="600" textAnchor="middle" fontFamily="Inter, sans-serif">
            {windSpeed}mph
          </text>
        </>
      )}

      {/* Dome indicator */}
      {dome && (
        <text x={homeX} y={arrowCy + 8} fill="#6b7280" fontSize="10" fontWeight="600" textAnchor="middle" fontFamily="Inter, sans-serif">
          DOME
        </text>
      )}

      {/* Precip probability */}
      {precipProb > 0 && (
        <text x={homeX} y={dome ? arrowCy + 20 : arrowCy + 26} fill="#60a5fa" fontSize="9" fontWeight="500" textAnchor="middle" fontFamily="Inter, sans-serif">
          {precipProb}% rain
        </text>
      )}
    </svg>
  );
};

// Weather badge component for game headers
const WeatherBadge = ({ weather, isMobile, homeTeam }) => {
  if (!weather) return null;
  const temp = weather.temperature;
  const wind = weather.windSpeed;
  const windDir = weather.windDirectionDegrees;
  const desc = weather.description || '';
  const cloudCover = weather.cloudCoverPercentage || 0;
  const precipProb = weather.precipitationProbability || 0;

  // Look up ballpark data by home team abbreviation
  const park = homeTeam ? BALLPARK_DATA[homeTeam] : null;

  if (park) {
    return (
      <BallparkWeatherSVG
        cfBearing={park.cfBearing}
        windFromDeg={windDir || 0}
        windSpeed={wind || 0}
        size={isMobile ? 56 : 70}
        dome={park.dome}
        temp={temp}
        description={desc}
        cloudCover={cloudCover}
        precipProb={precipProb}
      />
    );
  }

  // Fallback: simple text weather (non-MLB or unknown park)
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: isMobile ? '0.65rem' : '0.7rem',
      color: '#9ca3af'
    }}>
      <WeatherIcon size={isMobile ? 16 : 18} description={desc} cloudCover={cloudCover} precipProb={precipProb} temp={temp} />
      <span style={{ color: temp <= 40 ? '#60a5fa' : temp >= 85 ? '#dc3545' : '#dee2e6', fontWeight: 500 }}>{temp}°</span>
      {wind > 0 && <span>{wind}mph</span>}
      {precipProb > 0 && <span style={{ color: '#60a5fa' }}>{precipProb}%</span>}
    </div>
  );
};

const GameHeader = ({
  gameTime,
  gameStatus,
  gameDate,
  game,
  getTeamLogoUrl,
  isMobile,
  isCollapsed,
  onCollapseToggle,
  // Sport-specific customization props
  formatLiveStatus,
  showScores = true,
  isLoading = false
}) => {
  // Safety check - provide defaults if game data is not fully loaded
  const safeGame = { away: 'TBD', home: 'TBD', gameIDmlb: 'loading', ...game };

  const formatTime = (timeStr, gameStatus) => {
    if (!timeStr) return '';

    // If time is TBD, display TBD
    if (timeStr === 'TBD' || timeStr === 'TBA') return timeStr;

    // If the game is finished, show "Final"
    if (gameStatus && isGameFinished(gameStatus)) {
      return 'Final';
    }

    // For live games, let sport-specific handler format it
    if (gameStatus === 'Live' || gameStatus === 'In Progress' || gameStatus === 'InProgress') {
      return 'LIVE'; // Default, can be overridden by formatLiveStatus
    }

    // Handle other statuses
    if (gameStatus === 'Delayed') return 'Delayed';
    if (gameStatus === 'Postponed') return 'Postponed';

    // Try to format as time
    try {
      // Handle strings that already contain formatted time (e.g., "2025-11-02 08:20:00 PM EST")
      // Extract just the time portion
      const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*((?:AM|PM|am|pm))/i);
      if (timeMatch) {
        // Found a time with AM/PM, return just HH:MM AM/PM (without seconds)
        return `${timeMatch[1]}:${timeMatch[2]} ${timeMatch[3].toUpperCase()}`;
      }

      // Handle 24-hour time format (e.g., "19:30")
      if (timeStr.match(/^\d{1,2}:\d{2}$/)) {
        const [hours, minutes] = timeStr.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
      }

      // Try to parse as a date/time string
      // Safari requires ISO 8601 format, so convert space to 'T' if needed
      let dateStr = timeStr;
      if (typeof dateStr === 'string' && dateStr.includes(' ') && !dateStr.includes('T')) {
        // Convert "YYYY-MM-DD HH:MM:SS" to "YYYY-MM-DDTHH:MM:SS"
        dateStr = dateStr.replace(' ', 'T');
      }
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        const timeFormatted = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });

        // Day-of-week context is rendered on the sub-line below via
        // getDayOfWeek — keep the inline time itself unadorned so it doesn't
        // double-print.
        return timeFormatted;
      }

      // If all else fails, return the original string
      return timeStr;
    } catch (e) {
      return timeStr;
    }
  };

  // Get day of week for future games (returns null if today)
  const getDayOfWeek = (timeStr, gameStatus) => {
    if (gameStatus === 'Live' || gameStatus === 'In Progress' || gameStatus === 'InProgress' || isGameFinished(gameStatus)) {
      return null;
    }

    // Parse a date-only string ("YYYY-MM-DD" or "MM/DD/YYYY") in local time,
    // avoiding the UTC-midnight gotcha that would offset the day by one in
    // western timezones. Returns null when the string can't be parsed.
    const parseDateOnly = (s) => {
      if (typeof s !== 'string') return null;
      const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3]);
      const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (mdy) return new Date(+mdy[3], +mdy[1] - 1, +mdy[2]);
      return null;
    };

    // Try multiple date sources — gameTime often lacks a date on WNBA/NBA,
    // and the surrounding table passes its own gameDate prop we can fall back
    // on for the "next game" panel view.
    const dateSources = [
      timeStr,
      safeGame.startTimeEST,
      safeGame.commence_time,
      safeGame.date,
      safeGame.gameDate,
      gameDate
    ].filter(Boolean);

    for (const source of dateSources) {
      try {
        let parsed = parseDateOnly(source);
        if (!parsed) {
          // Safari requires ISO 8601 format, so convert space to 'T' if needed
          let dateStr = source;
          if (typeof dateStr === 'string' && dateStr.includes(' ') && !dateStr.includes('T')) {
            dateStr = dateStr.replace(' ', 'T');
          }
          parsed = new Date(dateStr);
        }
        if (parsed && !isNaN(parsed.getTime())) {
          // Check if the game date is not today
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const gameDay = new Date(parsed);
          gameDay.setHours(0, 0, 0, 0);

          // If game is in the future and not today
          if (gameDay > today) {
            const dayOfWeek = parsed.toLocaleDateString('en-US', { weekday: 'long' });
            // For games more than 7 days out, also append M/D
            const diffDays = (gameDay - today) / (1000 * 60 * 60 * 24);
            if (diffDays > 7) {
              return `${dayOfWeek} ${parsed.getMonth() + 1}/${parsed.getDate()}`;
            }
            return dayOfWeek;
          }
          // If we found a valid date but it's today, return null (no need to check other sources)
          return null;
        }
      } catch (e) {
        // Try next source
      }
    }

    return null;
  };

  return (
    <div
      className="game-header-container"
      style={{
        position: 'relative',
        cursor: isMobile ? 'pointer' : 'default',
        userSelect: 'none'
      }}
      onClick={isMobile ? () => onCollapseToggle && onCollapseToggle(!isCollapsed) : undefined}
    >
      {/* Game time/status in top left */}
      {!isMobile ? (
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start'
        }}>
          <div style={{
            fontSize: '1.4rem',
            color: '#9ca3af',
            fontWeight: '300'
          }}>
            {(() => {
              // Check both gameStatus prop and safeGame.status
              const currentStatus = gameStatus || safeGame.status;

              // Handle Live games - use custom formatter if provided
              if (isGameLive(currentStatus)) {
                // Check for statusDetail with shortDetail (NBA/ESPN format)
                if (safeGame.statusDetail?.type?.shortDetail) {
                  return safeGame.statusDetail.type.shortDetail;
                }

                // Check for period/clock directly on game object (NHL/NBA format)
                if (safeGame.period !== undefined || safeGame.clock || safeGame.displayClock) {
                  const sport = safeGame.sport?.toLowerCase();
                  let periodText = '';
                  let clockDisplay = safeGame.displayClock || safeGame.clock;

                  if (sport === 'nhl') {
                    // Hockey periods
                    if (safeGame.period === 1) periodText = '1st';
                    else if (safeGame.period === 2) periodText = '2nd';
                    else if (safeGame.period === 3) periodText = '3rd';
                    else if (safeGame.period === 4) periodText = 'OT';
                    else if (safeGame.period > 4) periodText = `OT${safeGame.period - 3}`;
                  } else if (sport === 'nba' || sport === 'wnba') {
                    // Basketball quarters
                    if (safeGame.period === 1) periodText = '1st';
                    else if (safeGame.period === 2) periodText = '2nd';
                    else if (safeGame.period === 3) periodText = '3rd';
                    else if (safeGame.period === 4) periodText = '4th';
                    else if (safeGame.period > 4) periodText = `OT${safeGame.period - 4}`;
                  } else if (sport === 'ncaab') {
                    // College basketball halves
                    if (safeGame.period === 1) periodText = '1st Half';
                    else if (safeGame.period === 2) periodText = '2nd Half';
                    else if (safeGame.period > 2) periodText = `OT${safeGame.period - 2}`;
                  } else if (sport === 'nfl' || sport === 'ncaaf') {
                    // Football quarters
                    if (safeGame.period === 1) periodText = '1st Qtr';
                    else if (safeGame.period === 2) periodText = '2nd Qtr';
                    else if (safeGame.period === 3) periodText = '3rd Qtr';
                    else if (safeGame.period === 4) periodText = '4th Qtr';
                    else if (safeGame.period > 4) periodText = `OT${safeGame.period - 4}`;
                  } else if (sport === 'soccer') {
                    // Soccer halves - use status or period
                    if (currentStatus?.toUpperCase().includes('FIRST HALF') || safeGame.period === 1) {
                      periodText = '1st Half';
                    } else if (currentStatus?.toUpperCase().includes('SECOND HALF') || safeGame.period === 2) {
                      periodText = '2nd Half';
                    } else if (currentStatus?.toUpperCase().includes('HALFTIME') || currentStatus === 'HT') {
                      periodText = 'Halftime';
                      clockDisplay = null; // No clock at halftime
                    } else if (currentStatus?.toUpperCase().includes('EXTRA TIME') || safeGame.period > 2) {
                      periodText = 'Extra Time';
                    } else if (currentStatus?.toUpperCase().includes('PENALTY')) {
                      periodText = 'Penalties';
                      clockDisplay = null;
                    } else {
                      periodText = currentStatus || '';
                    }
                  } else {
                    periodText = safeGame.period ? `P${safeGame.period}` : '';
                  }

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      {periodText && <span style={{ color: 'yellow', fontWeight: 600 }}>{periodText}</span>}
                      {clockDisplay && <span style={{ color: 'yellow', fontWeight: 500 }}>{clockDisplay}</span>}
                    </div>
                  );
                }

                if (formatLiveStatus) {
                  return formatLiveStatus(currentStatus, game, safeGame);
                }

                // Default LIVE indicator
                return (
                  <span style={{
                    backgroundColor: 'red',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    display: 'inline-block'
                  }}>
                    LIVE
                  </span>
                );
              }

              return formatTime(gameTime, currentStatus);
            })()}
          </div>
          {(() => {
            const currentStatus = gameStatus || safeGame.status;
            const dayOfWeek = getDayOfWeek(gameTime, currentStatus);
            if (dayOfWeek) {
              return (
                <div style={{
                  fontSize: '0.8rem',
                  color: '#6c757d',
                  fontWeight: '400',
                  marginTop: '2px'
                }}>
                  {dayOfWeek}
                </div>
              );
            }
            return null;
          })()}
        </div>
      ) : null}

      {/* Weather badge - desktop top right */}
      {!isMobile && safeGame.weatherForecast && (
        <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
          <WeatherBadge weather={safeGame.weatherForecast} isMobile={false} homeTeam={safeGame.home} />
        </div>
      )}

      {/* Collapse indicator for mobile */}
      {isMobile && (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {safeGame.weatherForecast && <WeatherBadge weather={safeGame.weatherForecast} isMobile={true} homeTeam={safeGame.home} />}
          <span style={{
            fontSize: '1rem',
            color: '#9ca3af',
            transition: 'transform 0.2s ease',
            transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </span>
        </div>
      )}
      
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        {/* Team logos and scores */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {/* Away Score */}
          {(() => {
            // Use ?? to preserve 0 scores (|| would skip 0 as falsy)
            const awayScore = safeGame.awayScore ?? safeGame.away_score ?? safeGame.awayTeamDetails?.score;
            const isLiveOrFinished = isGameLive(gameStatus) || isGameFinished(gameStatus) || isGameLive(safeGame.status) || isGameFinished(safeGame.status);
            if (showScores && isLiveOrFinished && awayScore !== null && awayScore !== undefined && awayScore !== '') {
              return (
                <span style={{ fontSize: isMobile ? '1.5rem' : '2em', fontWeight: 'bold', marginRight: '8px' }}>
                  {awayScore}
                </span>
              );
            }
            return null;
          })()}
          
          {/* Away Team Logo */}
          <img
            alt={safeGame.away}
            src={safeGame.awayLogo || getTeamLogoUrl(safeGame.away)}
            style={{ width: safeGame.sport === 'nhl' ? 40 : 32, height: safeGame.sport === 'nhl' ? 40 : 32, marginRight: 8 }}
            onError={(e) => e.target.style.display = 'none'}
          />

          {/* VS indicator or loading spinner */}
          {isLoading ? (
            <div
              className="spinner-border spinner-border-sm"
              role="status"
              style={{
                width: '16px',
                height: '16px',
                borderWidth: '2px',
                color: '#6c757d',
                margin: '0 4px'
              }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            '@'
          )}

          {/* Home Team Logo */}
          <img
            alt={safeGame.home}
            src={safeGame.homeLogo || getTeamLogoUrl(safeGame.home)}
            style={{ width: safeGame.sport === 'nhl' ? 40 : 32, height: safeGame.sport === 'nhl' ? 40 : 32, margin: '0 8px' }}
            onError={(e) => e.target.style.display = 'none'}
          />
          
          {/* Home Score */}
          {(() => {
            // Use ?? to preserve 0 scores (|| would skip 0 as falsy)
            const homeScore = safeGame.homeScore ?? safeGame.home_score ?? safeGame.homeTeamDetails?.score;
            const isLiveOrFinished = isGameLive(gameStatus) || isGameFinished(gameStatus) || isGameLive(safeGame.status) || isGameFinished(safeGame.status);
            if (showScores && isLiveOrFinished && homeScore !== null && homeScore !== undefined && homeScore !== '') {
              return (
                <span style={{ fontSize: isMobile ? '1.5rem' : '2em', fontWeight: 'bold', marginLeft: '8px' }}>
                  {homeScore}
                </span>
              );
            }
            return null;
          })()}
        </div>
        
        {/* Team names with optional inline records for NBA/WNBA/NHL and rankings for NCAAB */}
        <div style={{ fontSize: '0.8rem', color: '#6c757d', textAlign: 'center' }}>
          {(() => {
            const sport = safeGame.sport?.toLowerCase();
            const showInlineRecords = sport === 'nba' || sport === 'wnba' || sport === 'nhl';
            const isNcaab = sport === 'ncaab';

            const getOverallRecord = (teamDetails) => {
              if (!teamDetails?.records) return null;
              const overall = teamDetails.records.find(r => r.name === 'overall' || r.type === 'total');
              return overall?.summary || null;
            };

            const awayRecord = showInlineRecords ? getOverallRecord(safeGame.awayTeamDetails) : null;
            const homeRecord = showInlineRecords ? getOverallRecord(safeGame.homeTeamDetails) : null;

            // Get tip win percentage and rank for NBA
            const awayTipPct = sport === 'nba' && safeGame.awayTipStats?.tipWinPct != null
              ? Math.round(safeGame.awayTipStats.tipWinPct)
              : null;
            const homeTipPct = sport === 'nba' && safeGame.homeTipStats?.tipWinPct != null
              ? Math.round(safeGame.homeTipStats.tipWinPct)
              : null;
            const awayTipRank = sport === 'nba' && safeGame.awayTipStats?.rank != null
              ? safeGame.awayTipStats.rank
              : null;
            const homeTipRank = sport === 'nba' && safeGame.homeTipStats?.rank != null
              ? safeGame.homeTipStats.rank
              : null;

            // For NCAAB, show ranking if 25 or lower
            const awayRank = isNcaab && safeGame.awayRank && safeGame.awayRank <= 25 ? `#${safeGame.awayRank} ` : '';
            const homeRank = isNcaab && safeGame.homeRank && safeGame.homeRank <= 25 ? `#${safeGame.homeRank} ` : '';

            // Build record display with optional tip percentage and rank
            const awayTipDisplay = awayTipPct != null
              ? `${awayTipPct}%${awayTipRank != null ? ` #${awayTipRank}` : ''} tip`
              : null;
            const homeTipDisplay = homeTipPct != null
              ? `${homeTipPct}%${homeTipRank != null ? ` #${homeTipRank}` : ''} tip`
              : null;
            const awayRecordParts = [awayRecord, awayTipDisplay].filter(Boolean).join(', ');
            const homeRecordParts = [homeRecord, homeTipDisplay].filter(Boolean).join(', ');

            const awayDisplay = awayRank + safeGame.away + (awayRecordParts ? ` (${awayRecordParts})` : '');
            const homeDisplay = homeRank + safeGame.home + (homeRecordParts ? ` (${homeRecordParts})` : '');

            return `${awayDisplay} @ ${homeDisplay}`;
          })()}
        </div>

        {/* Team records (NCAAB - separate line with conference records) */}
        {safeGame.sport?.toLowerCase() === 'ncaab' && (safeGame.awayRecord || safeGame.homeRecord) && (() => {
          const getRecordDisplay = (records) => {
            if (!records || !Array.isArray(records)) return null;
            const overall = records.find(r => r.name === 'overall' || r.type === 'total');
            const conf = records.find(r => r.name === 'vs. Conf.' || r.type === 'vsconf');
            if (!overall) return null;
            return conf ? `${overall.summary} (${conf.summary})` : overall.summary;
          };
          const awayRecordDisplay = getRecordDisplay(safeGame.awayRecord);
          const homeRecordDisplay = getRecordDisplay(safeGame.homeRecord);
          if (!awayRecordDisplay && !homeRecordDisplay) return null;
          return (
            <div style={{ fontSize: '0.7rem', color: '#9ca3af', textAlign: 'center', marginTop: '2px' }}>
              {awayRecordDisplay || '-'} @ {homeRecordDisplay || '-'}
            </div>
          );
        })()}

        {/* League name for Soccer */}
        {safeGame.sport?.toLowerCase() === 'soccer' && safeGame.leagueName && (
          <div style={{ fontSize: '0.7rem', color: '#9ca3af', textAlign: 'center', marginTop: '2px' }}>
            {safeGame.leagueName}
          </div>
        )}

        {/* Game time for mobile */}
        {isMobile && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '4px' }}>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center' }}>
              {(() => {
                // Check both gameStatus prop and safeGame.status
                const currentStatus = gameStatus || safeGame.status;

                // Handle Live games
                if (isGameLive(currentStatus)) {
                  // Check for period/clock on game object (all sports)
                  if (safeGame.period !== undefined || safeGame.clock || safeGame.displayClock) {
                    const sport = safeGame.sport?.toLowerCase();
                    let periodText = '';
                    let clockDisplay = safeGame.displayClock || safeGame.clock;

                    if (sport === 'nhl') {
                      if (safeGame.period === 1) periodText = '1st';
                      else if (safeGame.period === 2) periodText = '2nd';
                      else if (safeGame.period === 3) periodText = '3rd';
                      else if (safeGame.period === 4) periodText = 'OT';
                      else if (safeGame.period > 4) periodText = `OT${safeGame.period - 3}`;
                    } else if (sport === 'nba' || sport === 'wnba') {
                      if (safeGame.period === 1) periodText = '1st';
                      else if (safeGame.period === 2) periodText = '2nd';
                      else if (safeGame.period === 3) periodText = '3rd';
                      else if (safeGame.period === 4) periodText = '4th';
                      else if (safeGame.period > 4) periodText = `OT${safeGame.period - 4}`;
                    } else if (sport === 'nfl' || sport === 'ncaaf') {
                      if (safeGame.period === 1) periodText = '1st';
                      else if (safeGame.period === 2) periodText = '2nd';
                      else if (safeGame.period === 3) periodText = '3rd';
                      else if (safeGame.period === 4) periodText = '4th';
                      else if (safeGame.period > 4) periodText = 'OT';
                    } else if (sport === 'ncaab') {
                      if (safeGame.period === 1) periodText = '1st Half';
                      else if (safeGame.period === 2) periodText = '2nd Half';
                      else if (safeGame.period > 2) periodText = `OT${safeGame.period - 2}`;
                    } else if (sport === 'soccer') {
                      clockDisplay = safeGame.displayClock || (safeGame.clock ? `${safeGame.clock}'` : null);
                      if (currentStatus?.toUpperCase().includes('FIRST HALF') || safeGame.period === 1) {
                        periodText = '1st Half';
                      } else if (currentStatus?.toUpperCase().includes('SECOND HALF') || safeGame.period === 2) {
                        periodText = '2nd Half';
                      } else if (currentStatus?.toUpperCase().includes('HALFTIME') || currentStatus === 'HT') {
                        return <span style={{ color: 'yellow', fontWeight: 600 }}>Halftime</span>;
                      } else if (currentStatus?.toUpperCase().includes('EXTRA TIME') || safeGame.period > 2) {
                        periodText = 'Extra Time';
                      } else if (currentStatus?.toUpperCase().includes('PENALTY')) {
                        return <span style={{ color: 'yellow', fontWeight: 600 }}>Penalties</span>;
                      } else {
                        periodText = currentStatus || 'LIVE';
                      }
                    } else {
                      periodText = safeGame.period ? `P${safeGame.period}` : '';
                    }

                    if (periodText || clockDisplay) {
                      return (
                        <span style={{ color: 'yellow', fontWeight: 600 }}>
                          {periodText}{periodText && clockDisplay ? ' ' : ''}{clockDisplay || ''}
                        </span>
                      );
                    }
                  }

                  if (formatLiveStatus) {
                    return formatLiveStatus(currentStatus, game, safeGame);
                  }
                  return 'LIVE';
                }

                // Handle finished games
                if (isGameFinished(currentStatus)) {
                  if (formatLiveStatus) {
                    return formatLiveStatus(currentStatus, game, safeGame);
                  }
                  return 'Final';
                }

                // Show game time for scheduled games
                return formatTime(gameTime, currentStatus);
              })()}
            </div>
            {(() => {
              const currentStatus = gameStatus || safeGame.status;
              const dayOfWeek = getDayOfWeek(gameTime, currentStatus);
              if (dayOfWeek) {
                return (
                  <div style={{
                    fontSize: '0.65rem',
                    color: '#6c757d',
                    textAlign: 'center',
                    marginTop: '2px'
                  }}>
                    {dayOfWeek}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export { WeatherBadge };
export default GameHeader;